import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { ArrowLeft, Save } from 'lucide-react';

const ProvinceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { control, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      code: '',
      comune_istat: '',
      latitude: null,
      longitude: null
    }
  });

  useEffect(() => {
    if (isEditing) {
      fetchProvince();
    }
  }, [isEditing, id]);

  const fetchProvince = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data: province, error } = await supabase
        .from('provinces')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      if (province) {
        setValue('name', province.name);
        setValue('code', province.code);
        setValue('comune_istat', province.comune_istat || '');
        setValue('latitude', province.latitude || null);
        setValue('longitude', province.longitude || null);
      }
    } catch (error) {
      console.error('Error fetching province:', error);
      toast.error('Errore nel caricamento del comune');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formData: any) => {
    setSubmitLoading(true);
    
    try {
      // Normalizza il codice in maiuscolo
      const normalizedData = {
        ...formData,
        code: formData.code.trim().toUpperCase()
      };
      
      if (isEditing) {
        const { error } = await supabase
          .from('provinces')
          .update(normalizedData)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Comune aggiornato con successo');
        navigate('/admin/provinces');
      } else {
        const { error } = await supabase
          .from('provinces')
          .insert(normalizedData);
        
        if (error) throw error;
        
        toast.success('Comune creato con successo');
        navigate('/admin/provinces');
      }
    } catch (error: any) {
      console.error('Error saving province:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante il salvataggio'}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Modifica Città/Comune" : "Nuova Città/Comune"}>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Modifica Città/Comune" : "Nuova Città/Comune"}>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/provinces')}
          className="text-gray-600 hover:text-gray-900 flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna alle città/comuni
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 pb-2 border-b">Informazioni Città/Comune</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome*
              </label>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. Firenze"
                  />
                )}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice Provincia/Città*
              </label>
              <Controller
                name="code"
                control={control}
                rules={{ required: 'Il codice è obbligatorio' }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. FI"
                    maxLength={5}
                    style={{ textTransform: 'uppercase' }}
                    onChange={e => {
                      field.onChange(e.target.value.toUpperCase());
                    }}
                  />
                )}
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Inserisci il codice provincia (es. FI, RM, MI).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice ISTAT Comune
              </label>
              <Controller
                name="comune_istat"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className={`w-full border ${errors.comune_istat ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. 048017"
                  />
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Codice ISTAT della città/comune (opzionale)</p>
              {errors.comune_istat && (
                <p className="text-red-500 text-sm mt-1">{errors.comune_istat.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitudine
              </label>
              <Controller
                name="latitude"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    step="0.000001"
                    className={`w-full border ${errors.latitude ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. 43.756111"
                  />
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Coordinata geografica (es. 43.756111 per Scandicci)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitudine
              </label>
              <Controller
                name="longitude"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="number"
                    step="0.000001"
                    className={`w-full border ${errors.longitude ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                    placeholder="Es. 11.178889"
                  />
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Coordinata geografica (es. 11.178889 per Scandicci)</p>
            </div>
          </div>
        </div>
        
        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/provinces')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitLoading ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Salva Comune
              </>
            )}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

export default ProvinceForm;